import { getAvailableMacros } from '@/lib/reportMacros';
import { useEffect, useMemo, useRef, useState } from 'react';
/**
 * Macros in any textarea: a shortcode expands on Space or Tab, Ctrl+/ (Cmd+/)
 * opens the picker, and the picker inserts at the caret.
 *
 * Wire `textareaRef` and `handleKeyDown` onto the textarea, and render a
 * MacroPopover with `insertAtCaret` and `picker` when `hasMacros` is true.
 * The field must be controlled by `value` / `onChange`.
 */
export function useMacroExpansion({ value, onChange, macros, templateId, readOnly, }) {
    const textareaRef = useRef(null);
    // Same macro set the picker lists: expansion must never fire for a macro
    // the user can't see in the picker.
    const availableMacros = useMemo(() => getAvailableMacros(macros, templateId), [macros, templateId]);
    // Caret position to restore after a programmatic insert. Applied once the
    // controlled value round-trips through the parent (jsdom + browsers both
    // reset the caret to the end when value is replaced wholesale).
    const pendingCaretRef = useRef(null);
    useEffect(() => {
        if (pendingCaretRef.current == null)
            return;
        const el = textareaRef.current;
        if (el) {
            el.focus();
            el.setSelectionRange(pendingCaretRef.current, pendingCaretRef.current);
        }
        pendingCaretRef.current = null;
    }, [value]);
    // Insert at the caret (replacing any selection), not at the end of the
    // field. Falls back to append when the textarea isn't mounted.
    const insertAtCaret = (text) => {
        const el = textareaRef.current;
        if (!el) {
            onChange(value ? `${value}\n${text}` : text);
            return;
        }
        const start = el.selectionStart ?? value.length;
        const end = el.selectionEnd ?? start;
        pendingCaretRef.current = start + text.length;
        onChange(value.slice(0, start) + text + value.slice(end));
    };
    const [pickerOpen, setPickerOpen] = useState(false);
    // Opened from the keyboard, the picker hands focus back to the field when
    // it closes (Escape included), so typing carries on where it left off.
    const pickerOpenedFromKeyboardRef = useRef(false);
    const onPickerOpenChange = (open) => {
        setPickerOpen(open);
        if (!open && pickerOpenedFromKeyboardRef.current)
            textareaRef.current?.focus();
        if (!open)
            pickerOpenedFromKeyboardRef.current = false;
    };
    // Shortcode expansion: the whitespace-delimited token immediately before
    // the caret, case-insensitively matching a shortcode, expands in place on
    // Space or Tab. The settings screen promises exactly this. Space keeps its
    // own character after the expansion; Tab is eaten. Shift+Tab, a token that
    // matches nothing and an IME composition keep the key's native behaviour.
    // Ctrl+/ (Cmd+/ on a Mac) opens the macro picker, as its tooltip says.
    const handleKeyDown = (e) => {
        if (readOnly || e.nativeEvent.isComposing)
            return;
        if (e.key === '/' && (e.ctrlKey || e.metaKey) && !e.altKey) {
            e.preventDefault();
            pickerOpenedFromKeyboardRef.current = true;
            setPickerOpen(true);
            return;
        }
        const isTab = e.key === 'Tab' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey;
        const isSpace = e.key === ' ' && !e.ctrlKey && !e.metaKey && !e.altKey;
        if (!isTab && !isSpace)
            return;
        const el = e.currentTarget;
        const caret = el.selectionStart;
        if (caret == null || caret === 0 || el.selectionEnd !== caret)
            return;
        const tokenMatch = /(\S+)$/.exec(value.slice(0, caret));
        if (!tokenMatch)
            return;
        const token = tokenMatch[1].toLowerCase();
        const macro = availableMacros.find((m) => m.shortcode.toLowerCase() === token);
        if (!macro)
            return;
        e.preventDefault();
        const start = caret - tokenMatch[1].length;
        const inserted = isSpace ? `${macro.content} ` : macro.content;
        pendingCaretRef.current = start + inserted.length;
        onChange(value.slice(0, start) + inserted + value.slice(caret));
    };
    return {
        textareaRef,
        handleKeyDown,
        insertAtCaret,
        hasMacros: availableMacros.length > 0,
        picker: { open: pickerOpen, onOpenChange: onPickerOpenChange },
    };
}
